import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Database, HardDrive, FileStack, Clock, RefreshCw, Search,
  ChevronDown, ChevronRight, AlertTriangle, CheckCircle, Layers
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatBytes(bytes) {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

function timeAgo(isoStr) {
  if (!isoStr) return 'No activity';
  const diff = Date.now() - new Date(isoStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

function SummaryCard({ icon: Icon, label, value, color }) {
  return (
    <Card className="bg-slate-800/60 border-slate-700">
      <CardContent className="p-5 flex items-center gap-4">
        <div className={`p-2.5 rounded-lg ${color}`}>
          <Icon size={20} className="text-white" />
        </div>
        <div>
          <p className="text-2xl font-bold text-white tracking-tight">{value}</p>
          <p className="text-xs text-slate-400 uppercase tracking-wider mt-0.5">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function TenantRow({ tenant }) {
  const [expanded, setExpanded] = useState(false);
  const isActive = tenant.status === 'active';
  const activityAge = timeAgo(tenant.last_activity);
  const isStale = !tenant.last_activity || (Date.now() - new Date(tenant.last_activity).getTime()) > 30 * 86400000;

  return (
    <div className="border border-slate-700/60 rounded-lg overflow-hidden" data-testid={`db-tenant-${tenant.slug}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-slate-800/40 transition-colors text-left"
        data-testid={`db-expand-${tenant.slug}`}
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className={`w-2 h-2 rounded-full shrink-0 ${isActive ? 'bg-emerald-400' : 'bg-slate-500'}`} />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white truncate">
              {tenant.name}
              {tenant.co_located && (
                <span
                  className="ml-2 inline-flex items-center rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-300 ring-1 ring-amber-500/30"
                  title="This tenant shares the platform database. Atlas didn't allow a dedicated per-tenant DB."
                  data-testid={`co-located-badge-${tenant.slug}`}
                >
                  co-located
                </span>
              )}
            </p>
            <p className="text-xs text-slate-500 font-mono">{tenant.db_name}</p>
          </div>
        </div>

        <div className="flex items-center gap-6 shrink-0">
          <div className="text-right hidden sm:block">
            <p className="text-sm font-bold text-white">{tenant.total_docs.toLocaleString()}</p>
            <p className="text-[10px] text-slate-500">docs</p>
          </div>
          <div className="text-right hidden md:block">
            <p className="text-sm font-bold text-white">{tenant.total_collections}</p>
            <p className="text-[10px] text-slate-500">collections</p>
          </div>
          <div className="text-right hidden md:block">
            <p className="text-sm font-bold text-white">{formatBytes(tenant.size_bytes)}</p>
            <p className="text-[10px] text-slate-500">size</p>
          </div>
          <div className="text-right hidden lg:block w-20">
            <div className={`flex items-center gap-1 justify-end ${isStale ? 'text-amber-400' : 'text-slate-300'}`}>
              {isStale && <AlertTriangle size={12} />}
              <p className="text-xs">{activityAge}</p>
            </div>
          </div>
          <Badge variant="outline" className={`text-[10px] border-slate-600 ${
            tenant.plan === 'enterprise' ? 'text-violet-400 border-violet-600/50' :
            tenant.plan === 'pro' ? 'text-blue-400 border-blue-600/50' :
            tenant.plan === 'starter' ? 'text-emerald-400 border-emerald-600/50' :
            'text-slate-400'
          }`}>{tenant.plan}</Badge>
          {expanded ? <ChevronDown size={16} className="text-slate-500" /> : <ChevronRight size={16} className="text-slate-500" />}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-slate-700/40 bg-slate-900/40 p-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
            {tenant.collections.map((coll) => (
              <div
                key={coll.name}
                className="p-2.5 bg-slate-800/60 rounded border border-slate-700/50 hover:border-slate-600 transition-colors"
              >
                <p className="text-xs font-mono text-slate-300 truncate" title={coll.name}>{coll.name}</p>
                <p className="text-lg font-bold text-white mt-1">{coll.documents.toLocaleString()}</p>
                {coll.last_updated && (
                  <p className="text-[10px] text-slate-500 mt-0.5">{timeAgo(coll.last_updated)}</p>
                )}
              </div>
            ))}
          </div>
          {tenant.collections.length === 0 && (
            <p className="text-sm text-slate-500 text-center py-4">No collections found</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function SuperAdminDbHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');

  const token = localStorage.getItem('superAdminToken');
  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('superAdminToken')}` });

  const fetchData = useCallback(async (showToast) => {
    if (showToast) setRefreshing(true);
    try {
      const res = await axios.get(`${API}/platform/db-health`, { headers: getHeaders() });
      setData(res.data);
      if (showToast) toast.success('Database health refreshed');
    } catch {
      toast.error('Failed to fetch database health');
    }
    setLoading(false);
    setRefreshing(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchData(false); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="db-health-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500" />
      </div>
    );
  }

  const summary = data?.summary || {};
  const tenants = (data?.tenants || []).filter((t) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return t.name.toLowerCase().includes(q) || t.slug.toLowerCase().includes(q) || t.db_name.includes(q);
  });

  const activeTenants = (data?.tenants || []).filter(t => t.status === 'active').length;
  const staleTenants = (data?.tenants || []).filter(t =>
    !t.last_activity || (Date.now() - new Date(t.last_activity).getTime()) > 30 * 86400000
  ).length;

  return (
    <div className="space-y-6 max-w-6xl" data-testid="db-health-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-white tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Database Health
          </h1>
          <p className="text-slate-400 mt-1">Per-tenant storage, collection stats, and activity monitoring</p>
        </div>
        <Button
          onClick={() => fetchData(true)}
          disabled={refreshing}
          className="bg-slate-700 hover:bg-slate-600 text-white"
          data-testid="refresh-db-health-btn"
        >
          <RefreshCw size={14} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard icon={Database} label="Tenant Databases" value={summary.total_tenants || 0} color="bg-violet-600" />
        <SummaryCard icon={FileStack} label="Total Documents" value={(summary.total_documents || 0).toLocaleString()} color="bg-blue-600" />
        <SummaryCard icon={Layers} label="Total Collections" value={summary.total_collections || 0} color="bg-emerald-600" />
        <SummaryCard icon={HardDrive} label="Total Size" value={formatBytes(summary.total_size_bytes)} color="bg-amber-600" />
      </div>

      {/* Activity Insight Bar */}
      <div className="flex items-center gap-4 text-sm">
        <div className="flex items-center gap-1.5">
          <CheckCircle size={14} className="text-emerald-400" />
          <span className="text-slate-300">{activeTenants} active</span>
        </div>
        {staleTenants > 0 && (
          <div className="flex items-center gap-1.5">
            <AlertTriangle size={14} className="text-amber-400" />
            <span className="text-amber-400">{staleTenants} inactive (&gt;30d)</span>
          </div>
        )}
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <Input
          placeholder="Search tenants..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 bg-slate-800/60 border-slate-700 text-white placeholder:text-slate-500"
          data-testid="db-health-search"
        />
      </div>

      {/* Tenant List */}
      <div className="space-y-2">
        {tenants.map((tenant) => (
          <TenantRow key={tenant.tenant_id} tenant={tenant} />
        ))}
        {tenants.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            {search ? 'No tenants match your search' : 'No tenant databases found'}
          </div>
        )}
      </div>
    </div>
  );
}
